<script setup lang="ts">
/**
 * DronePanel — Drone management with active count controls
 *
 * Shows bandwidth/bay/active stats, drone list with +/- buttons
 * to adjust how many of each type are active (respecting bandwidth and max active).
 */
import { computed, ref } from 'vue';
import { useFitStore } from '../../stores/fitStore';
import { iconUrl } from '../../composables/useApi';
import type { FitDrone } from '../../../shared/types';
import DroneQtyDialog from '../modals/DroneQtyDialog.vue';

const store = useFitStore();

const drones = computed(() => store.activeFit?.drones ?? []);

const bandwidthTotal = computed(() => store.activeStats?.resources?.droneBay?.bandwidth?.total ?? 0);
const bandwidthUsed = computed(() =>
  drones.value.reduce((sum, d) => sum + (d.bandwidth ?? 0) * d.activeCount, 0)
);
const bayTotal = computed(() => store.activeStats?.resources?.droneBay?.capacity?.total ?? 0);
const bayUsed = computed(() =>
  drones.value.reduce((sum, d) => sum + (d.volume ?? 0) * d.quantity, 0)
);
const maxActive = computed(() => store.activeFit?.maxActiveDrones ?? 5);
const activeCount = computed(() =>
  drones.value.reduce((sum, d) => sum + d.activeCount, 0)
);

// Drag-drop
function handleDragOver(e: DragEvent): void {
  e.preventDefault();
  if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
}

const showQtyDialog = ref(false);
const pendingDrone = ref<{ typeID: number; typeName: string } | null>(null);

function handleDrop(e: DragEvent): void {
  e.preventDefault();
  try {
    const data = e.dataTransfer?.getData('text/plain');
    if (!data) return;
    const parsed = JSON.parse(data);
    if (parsed.typeID) {
      pendingDrone.value = { typeID: parsed.typeID, typeName: parsed.typeName };
      showQtyDialog.value = true;
    }
  } catch { /* ignore */ }
}

function confirmDroneQty(qty: number): void {
  if (pendingDrone.value) {
    store.addDroneToFit(pendingDrone.value.typeID, pendingDrone.value.typeName, qty);
  }
  showQtyDialog.value = false;
  pendingDrone.value = null;
}

function cancelDroneQty(): void {
  showQtyDialog.value = false;
  pendingDrone.value = null;
}

function removeDrone(drone: FitDrone): void {
  store.removeDroneFromFit(drone.id);
}

function adjustActive(drone: FitDrone, delta: number): void {
  const newCount = Math.max(0, Math.min(drone.quantity, drone.activeCount + delta));
  if (newCount !== drone.activeCount) {
    store.setDroneActiveCount(drone.id, newCount);
  }
}

function activateAll(drone: FitDrone): void {
  store.setDroneActiveCount(drone.id, drone.quantity);
}

function deactivateAll(drone: FitDrone): void {
  store.setDroneActiveCount(drone.id, 0);
}
</script>

<template>
  <div class="drone-panel" @drop="handleDrop" @dragover="handleDragOver">
    <!-- Stats bar -->
    <div class="drone-stats-bar">
      <div class="stat-pill" :class="{ over: bandwidthUsed > bandwidthTotal }">
        <span class="stat-icon">📡</span>
        <span>BW {{ bandwidthUsed.toFixed(0) }}/{{ bandwidthTotal.toFixed(0) }} Mbit/s</span>
      </div>
      <div class="stat-pill" :class="{ over: bayUsed > bayTotal }">
        <span class="stat-icon">📦</span>
        <span>Bay {{ bayUsed.toFixed(0) }}/{{ bayTotal.toFixed(0) }} m³</span>
      </div>
      <div class="stat-pill" :class="{ over: activeCount > maxActive }">
        <span class="stat-icon">🛰</span>
        <span>Active {{ activeCount }}/{{ maxActive }}</span>
      </div>
    </div>

    <!-- Drone list -->
    <div v-if="drones.length > 0" class="drone-list">
      <div
        v-for="drone in drones"
        :key="drone.id"
        class="drone-row"
        :class="{ inactive: drone.activeCount === 0 }"
      >
        <img :src="iconUrl(drone.typeID, 32)" class="drone-icon" :alt="drone.typeName" />

        <div class="drone-info">
          <span class="drone-name">{{ drone.typeName }}</span>
          <span class="drone-meta">
            x{{ drone.quantity }}
            · {{ (drone.bandwidth * drone.activeCount).toFixed(0) }} Mbit/s
          </span>
        </div>

        <!-- Active count controls: -/count/+ -->
        <div class="active-controls">
          <button
            class="adj-btn"
            :disabled="drone.activeCount <= 0"
            @click.stop="adjustActive(drone, -1)"
            title="Deactivate one"
          >−</button>
          <span
            class="active-count"
            :class="{ none: drone.activeCount === 0, full: drone.activeCount === drone.quantity }"
            :title="`${drone.activeCount} of ${drone.quantity} active`"
          >{{ drone.activeCount }}</span>
          <button
            class="adj-btn"
            :disabled="drone.activeCount >= drone.quantity"
            @click.stop="adjustActive(drone, 1)"
            title="Activate one"
          >+</button>
        </div>

        <button class="drone-remove" @click.stop="removeDrone(drone)" title="Remove drone">&times;</button>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else class="drone-empty">
      Drag drones from the Market browser
    </div>

    <!-- Quantity dialog for adding new drones -->
    <DroneQtyDialog
      :visible="showQtyDialog"
      :type-name="pendingDrone?.typeName ?? ''"
      @confirm="confirmDroneQty"
      @cancel="cancelDroneQty"
    />
  </div>
</template>

<style scoped>
.drone-panel {
  padding: 8px;
  min-height: 80px;
}

/* Stats bar */
.drone-stats-bar {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.stat-pill {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: var(--bg-darker);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  font-size: 0.78rem;
  color: var(--text-secondary);
}

.stat-pill.over {
  color: var(--accent-red);
  border-color: var(--accent-red);
  background: rgba(244, 67, 54, 0.08);
}

.stat-icon { font-size: 0.7rem; }

/* Drone list */
.drone-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.drone-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  background: var(--bg-panel);
  border: 1px solid var(--border-color);
  border-left: 3px solid var(--accent-green);
  border-radius: var(--radius-sm);
  transition: all var(--transition-fast);
}

.drone-row.inactive {
  opacity: 0.55;
  border-left-color: var(--text-secondary);
}

.drone-row:hover { background: var(--bg-hover); }

.drone-icon {
  width: 32px;
  height: 32px;
  border-radius: 3px;
  flex-shrink: 0;
}

.drone-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.drone-name {
  font-size: 0.85rem;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.drone-meta {
  font-size: 0.75rem;
  color: var(--text-secondary);
}

/* Active count controls */
.active-controls {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

.adj-btn {
  width: 22px;
  height: 22px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  background: var(--bg-darker);
  color: var(--text-primary);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--transition-fast);
  padding: 0;
  line-height: 1;
}

.adj-btn:hover:not(:disabled) {
  background: var(--bg-hover);
  border-color: var(--accent-blue);
  transform: scale(1.1);
}

.adj-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.active-count {
  min-width: 20px;
  text-align: center;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--accent-green);
}

.active-count.none { color: var(--text-secondary); }
.active-count.full { color: var(--accent-blue); }

/* Remove button */
.drone-remove {
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  font-size: 1rem;
  cursor: pointer;
  border-radius: var(--radius-sm);
  opacity: 0;
  transition: all var(--transition-fast);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.drone-row:hover .drone-remove { opacity: 1; }

.drone-remove:hover {
  background: rgba(244, 67, 54, 0.2);
  color: var(--accent-red);
}

/* Empty state */
.drone-empty {
  text-align: center;
  color: var(--text-secondary);
  font-size: 0.85rem;
  padding: 24px 8px;
}
</style>
